import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { 
  getUserAccessibleProjects, 
  getRelatedPeopleForUser, 
  getPersonProjectMatchDetails, 
  maskEmail 
} from '../utils/userPermissions';
import { 
  ArrowLeft, 
  Users, 
  Target, 
  Calendar, 
  Mail, 
  Phone, 
  Star, 
  Briefcase, 
  TrendingUp, 
  Award,
  ShieldAlert,
  Sparkles,
  MapPin
} from 'lucide-react';

const PersonProfile = () => {
  const { id } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  // Load live people and projects data with live fetch
  const [people, setPeople] = useState(() => JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
  const [projects, setProjects] = useState(() => JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));

  useEffect(() => {
    api.getPeople().then(data => {
      if (Array.isArray(data)) setPeople(data);
    });
    api.getProjects().then(data => {
      if (Array.isArray(data)) setProjects(data);
    });

    const handleStorageChange = () => {
      setPeople(JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
      setProjects(JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Get user's published project scope
  const userProjects = getUserAccessibleProjects(projects, currentUser, isAdmin);
  const accessiblePeople = getRelatedPeopleForUser(people, userProjects, currentUser, isAdmin);

  // Find person by ID or Email
  const person = people.find(p => String(p.id) === String(id) || p.email === id);

  // Determine authorization
  const isAccessible = isAdmin || 
                       currentUser?.email === person?.email || 
                       accessiblePeople.some(p => String(p.id) === String(id));

  if (!person) {
    return (
      <div className="card text-center py-16 max-w-lg mx-auto">
        <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Member Not Found</h2>
        <p className="text-gray-400 mb-6">The requested person profile does not exist.</p>
        <Link to="/people" className="btn-primary">
          Back to People
        </Link>
      </div>
    );
  }

  // Enforce privacy and data isolation for regular users
  if (!isAccessible) {
    return (
      <div className="card text-center py-16 px-6 max-w-lg mx-auto border border-amber-500/30">
        <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">Profile Access Restricted</h2>
        <p className="text-gray-400 text-sm mb-6">
          As a standard participant, you can only view profiles and details of people who are collaborating on your published projects or whose skills match your project requirements.
        </p>
        <div className="flex justify-center space-x-3">
          <Link to="/people" className="btn-secondary">
            View Related Talent
          </Link>
          <Link to="/projects/new" className="btn-primary">
            Publish New Project
          </Link>
        </div>
      </div>
    );
  }

  const matchInfo = getPersonProjectMatchDetails(person, userProjects);
  const initials = person.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'skills', label: 'Skills', icon: Target },
    { id: 'projects', label: 'Projects', icon: Briefcase },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/people"
            className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold">{person.name}</h1>
              {!isAdmin && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Related Talent
                </span>
              )}
            </div>
            <p className="text-dark-300">{person.role} • {person.team}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Link
            to={`/match?person=${person.id}`}
            className="btn-primary flex items-center"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Match to My Project
          </Link>
        </div>
      </div>

      {/* Project Relation Banner */}
      {!isAdmin && matchInfo.matchingProjects.length > 0 && (
        <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm flex items-center space-x-3">
          <Sparkles className="h-5 w-5 text-indigo-400 shrink-0" />
          <div>
            <span className="font-semibold text-white">Project Match: </span>
            This specialist has matching skills for your published project{' '}
            <span className="font-semibold underline">
              {matchInfo.matchingProjects.map(p => p.name).join(', ')}
            </span>. Contact details are privacy-protected.
          </div>
        </div>
      )}

      {/* Profile Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile Card */}
        <div className="lg:col-span-1">
          <div className="card text-center">
            {person.avatar && (person.avatar.startsWith('http://') || person.avatar.startsWith('https://')) ? (
              <img
                src={person.avatar}
                alt={person.name}
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-2 border-primary-500 shadow-lg"
              />
            ) : person.avatar ? (
              <div className="w-24 h-24 rounded-full bg-dark-800 border-2 border-primary-500/40 flex items-center justify-center text-4xl mx-auto mb-4 shadow-lg">
                {person.avatar}
              </div>
            ) : (
              <div className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold text-3xl mx-auto mb-4 shadow-lg">
                {initials}
              </div>
            )}
            
            <h2 className="text-xl font-semibold mb-1">{person.name}</h2>
            <p className="text-dark-300 mb-4">{person.role}</p>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-center space-x-2">
                <Mail className="h-4 w-4 text-dark-400" />
                <span>{isAdmin ? person.email : maskEmail(person.email)}</span>
              </div>
              {isAdmin && person.phone && (
                <div className="flex items-center justify-center space-x-2">
                  <Phone className="h-4 w-4 text-dark-400" />
                  <span>{person.phone}</span>
                </div>
              )}
              <div className="flex items-center justify-center space-x-2">
                <Calendar className="h-4 w-4 text-dark-400" />
                <span>{person.experience} experience</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Users className="h-4 w-4 text-dark-400" />
                <span>{person.availability}</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <MapPin className="h-4 w-4 text-dark-400" />
                <span>{person.location || 'Remote'}</span>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-dark-800">
              <div className="flex items-center justify-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(person.rating || 4.5) ? 'text-yellow-400 fill-current' : 'text-dark-600'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-dark-300">{person.rating || '4.5'}/5 rating</span>
            </div>
          </div>
        </div>

        {/* Stats and Skills */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card text-center">
              <div className="text-2xl font-bold text-purple-400">{person.skills?.length || 0}</div>
              <div className="text-sm text-dark-300">Verified Skills</div>
            </div>
            
            <div className="card text-center">
              <div className="text-2xl font-bold text-blue-400">{matchInfo.matchingProjects.length}</div>
              <div className="text-sm text-dark-300">Matching Projects</div>
            </div>
            
            <div className="card text-center">
              <div className="text-2xl font-bold text-green-400">{person.rating || '4.5'}</div>
              <div className="text-sm text-dark-300">Rating</div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Availability & Department</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-dark-300">Status:</span>
                <p className="font-medium text-green-400">{person.availability}</p>
              </div>
              <div>
                <span className="text-dark-300">Team / Track:</span>
                <p className="font-medium">{person.team}</p>
              </div>
              <div>
                <span className="text-dark-300">Location:</span>
                <p className="font-medium">{person.location || 'Remote'}</p>
              </div>
              <div>
                <span className="text-dark-300">Experience:</span>
                <p className="font-medium">{person.experience}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-dark-800">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-dark-300 hover:text-white hover:border-dark-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">About</h3>
                <p className="text-dark-300 leading-relaxed">
                  {person.bio || `${person.name} is a specialist in ${person.team} focusing on ${person.role}. Has expertise across key technologies and is actively contributing across project teams.`}
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">Skill Highlights</h3>
                <div className="flex flex-wrap gap-2">
                  {(person.skills || []).map((skill) => (
                    <span key={skill} className="px-3 py-1.5 rounded-lg bg-dark-800 text-white text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {person.certifications && person.certifications.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Award className="h-5 w-5 mr-2 text-yellow-400" />
                    Certifications & Badges
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {person.certifications.map((cert, idx) => {
                      const certObj = typeof cert === 'string' ? { name: cert, issuer: 'Verified Issuer' } : cert;
                      return (
                        <div key={idx} className="p-3 rounded-xl bg-dark-900 border border-dark-800 flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-400">
                            <Award className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-semibold text-white text-sm">{certObj.name}</div>
                            <div className="text-xs text-dark-400">{certObj.issuer || 'Credential'}{certObj.year ? ` • ${certObj.year}` : ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Skills & Expertise</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {(person.skills || []).map((skill) => {
                  const isMatch = matchInfo.matchedSkills.includes(skill.toLowerCase());
                  return (
                    <div key={skill} className={`p-4 rounded-xl border ${
                      isMatch 
                        ? 'bg-indigo-500/10 border-indigo-500/30 text-white' 
                        : 'bg-dark-900 border-dark-800 text-dark-200'
                    }`}>
                      <div className="font-semibold text-base mb-1">{skill}</div>
                      <div className="text-xs text-dark-400">
                        {isMatch ? '✨ Matches your project demand' : 'Verified competence'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Matching Projects</h3>
              {matchInfo.matchingProjects.length > 0 ? (
                <div className="space-y-4">
                  {matchInfo.matchingProjects.map((p) => (
                    <div key={p.id} className="p-4 rounded-xl bg-dark-900 border border-dark-800 flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-lg">{p.name}</h4>
                        <p className="text-sm text-dark-400">Overlapping skill requirements</p>
                      </div>
                      <Link to={`/projects/${p.id}`} className="btn-secondary text-sm">
                        View Project →
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-dark-400 text-sm">
                  {isAdmin ? 'No active project assignments recorded.' : 'No overlapping projects found.'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonProfile;
